"use client";

import React, { useState } from "react";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";
import Image from "next/image";
import { ApiKeyModal } from "./ApiKeyModal";
import { apiClient } from "@/lib/api-client";
import { toast } from "@/components/ui/use-toast";
import {
  trackDeviceConnected,
  trackDeviceDisconnected,
  trackPermissionGranted
} from "@/utils/analytics/wearable-events";

interface WearableDevice {
  id: string;
  name: string;
  description: string;
  logo: string;
  features: string[];
}

const wearableDevices: WearableDevice[] = [
  {
    id: "whoop",
    name: "WHOOP",
    description: "24/7 health monitoring with focus on recovery and strain",
    logo: "/wearable-images/whoop.jpg",
    features: ["Recovery Tracking", "Strain Coach", "Sleep Analysis", "HRV Monitoring"],
  },
  {
    id: "oura",
    name: "Oura Ring",
    description: "Advanced sleep tracking and readiness monitoring",
    logo: "/wearable-images/oura.jpg",
    features: ["Sleep Analysis", "Readiness Score", "Activity Tracking", "Temperature Monitoring"],
  },
  {
    id: "garmin",
    name: "Garmin",
    description: "Comprehensive fitness and outdoor activity tracking",
    logo: "/wearable-images/garmin.jpg",
    features: ["GPS Tracking", "Heart Rate", "VO2 Max", "Training Status"],
  },
  {
    id: "fitbit",
    name: "Fitbit",
    description: "All-day activity and health tracking",
    logo: "/wearable-images/fitbit.jpg",
    features: ["Step Counting", "Sleep Tracking", "Heart Rate", "Exercise Tracking"],
  },
  {
    id: "apple-watch",
    name: "Apple Watch",
    description: "Comprehensive health and fitness companion for iPhone users",
    logo: "/wearable-images/apple watch.jpg",
    features: ["ECG", "Blood Oxygen", "Activity Rings", "Workout Detection"],
  },
  {
    id: "samsung",
    name: "Samsung Galaxy Watch",
    description: "Advanced health tracking for Android and iOS users",
    logo: "/wearable-images/samsung galaxy.webp",
    features: ["BioActive Sensor", "Sleep Coaching", "Body Composition", "Workout Tracking"],
  },
];

interface WearableCardProps {
  device: WearableDevice;
  onConnectClick: (device: WearableDevice) => void;
}

const WearableCard: React.FC<WearableCardProps> = ({ device, onConnectClick }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Card className="h-full hover:shadow-lg transition-shadow duration-300">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>{device.name}</CardTitle>
            <div className="w-12 h-12 relative rounded-lg overflow-hidden">
              <Image
                src={device.logo}
                alt={`${device.name} logo`}
                layout="fill"
                objectFit="contain"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground mb-4">{device.description}</p>
          <div className="space-y-2 mb-6">
            {device.features?.map((feature, index) => (
              <div key={index} className="flex items-center text-sm">
                <span className="w-1.5 h-1.5 bg-primary rounded-full mr-2" />
                {feature}
              </div>
            ))}
          </div>
          <Button
            onClick={() => onConnectClick(device)}
            className="w-full"
          >
            Connect {device.name}
          </Button>
        </CardContent>
      </Card>
    </motion.div>
  );
};

interface WearableDeviceManagerProps {
  // Use a string prop instead of a function for serialization
  onConnectAction?: 'refresh' | 'redirect';
  redirectUrl?: string;
}

export function WearableDeviceManager({
  onConnectAction = 'refresh',
  redirectUrl = '/dashboard'
}: WearableDeviceManagerProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDevice, setSelectedDevice] = useState<WearableDevice | null>(null);
  const [showApiKeyModal, setShowApiKeyModal] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);

  const handleConnect = async (apiKey: string) => {
    if (!selectedDevice) return;
    
    setIsConnecting(true);
    try {
      // Track permission granted event
      trackPermissionGranted(
        selectedDevice.id,
        selectedDevice.id, // Using id as device type
        ['data_access', 'api_access'],
        { connection_method: 'api_key' }
      );
      
      const response = await apiClient.connectWearable({
        provider: selectedDevice.id,
        apiKey: apiKey
      });

      const data = response;

      // Track device connected event
      trackDeviceConnected(
        data.connection?.id || selectedDevice.id,
        selectedDevice.id,
        {
          connection_method: 'api_key',
          features: selectedDevice.features
        }
      );

      toast({
        title: "Device Connected",
        description: `Successfully connected to ${selectedDevice.name}`,
      });

      setShowApiKeyModal(false);
      setSelectedDevice(null);
      
      // Handle the connection action based on the prop
      if (onConnectAction === 'redirect' && redirectUrl) {
        if (window?.location) { window.location.href = redirectUrl; }
      } else {
        // Default to refresh
        window?.location?.reload();
      }
    } catch (error) {
      console.error('Error connecting to wearable:', error);
      toast({
        title: "Connection Failed",
        description: error instanceof Error ? error.message : "Failed to connect device",
        variant: "destructive",
      });
    } finally {
      setIsConnecting(false);
    }
  };

  const handleDeviceClick = (device: WearableDevice) => {
    setSelectedDevice(device);
    setShowApiKeyModal(true);
  };

  const handleModalClose = () => {
    if (!isConnecting) {
      setShowApiKeyModal(false);
      setSelectedDevice(null);
    }
  };

  const filteredDevices = wearableDevices.filter(device =>
    device?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    device?.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    device?.features?.some(feature =>
      feature.toLowerCase().includes(searchQuery.toLowerCase())
    )
  );

  return (
    <>
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold mb-4">Connect a Wearable Device</h2>
          <div className="relative">
            <Input
              placeholder="Search devices..."
              value={searchQuery}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e?.target?.value)}
              className="pl-10"
            />
            <Search className="h-4 w-4 absolute left-3 top-3 text-muted-foreground" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {filteredDevices?.map((device) => (
            <WearableCard
              key={device.id}
              device={device}
              onConnectClick={handleDeviceClick}
            />
          ))}
        </div>
      </div>

      {selectedDevice && (
        <ApiKeyModal
          isOpen={showApiKeyModal}
          onClose={handleModalClose}
          onSubmit={handleConnect}
          deviceName={selectedDevice.name}
          isLoading={isConnecting}
        />
      )}
    </>
  );
}